import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import type { IconComponent } from '../../types/api';
import { 
  Users, 
  Briefcase, 
  UserCheck, 
  UsersRound,
  BarChart3,
  UserPlus,
  Menu,
  X,
  LogOut,
  Sparkles,
  LifeBuoy,
  Edit3,
  Database,
  Settings,
  TrendingUp,
  Key,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Bot,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAuthSettings } from '../../context/AuthSettingsContext';
import { Modal, Button, Input, Tooltip } from '../ui';
import { ReportProblemModal } from '../common/ReportProblemModal';
import { NewFeature } from '../../whats-new/NewFeature';
import { track } from '../../lib/track';
import { CLAUDE_GUIDE_URL } from '../../lib/links';
import { authApi } from '../../api/auth';
import { useSidebar } from '../../hooks/useSidebar';
import logoEquancy from '../../assets/logo_equancy.png';
import logoMark from '../../assets/logo_e.png';

interface NavigationItem {
  name: string;
  href: string;
  icon: IconComponent;
  roles: string[];
}

interface NavigationSection {
  name: string;
  icon: IconComponent;
  items: NavigationItem[];
}

const navigationSections: NavigationSection[] = [
  {
    name: 'Staffing',
    icon: TrendingUp,
    items: [
      { name: 'Dashboard', href: '/staffing', icon: BarChart3, roles: ['superadmin', 'manager', 'consultant'] },
      { name: 'Saisie', href: '/saisie-staffing', icon: Edit3, roles: ['superadmin', 'manager'] },
    ]
  },
  {
    name: 'Base de données',
    icon: Database,
    items: [
      { name: 'Clients', href: '/clients', icon: Users, roles: ['superadmin', 'manager'] },
      { name: 'Projets', href: '/projects', icon: Briefcase, roles: ['superadmin', 'manager'] },
      { name: 'Consultants', href: '/consultants', icon: UserCheck, roles: ['superadmin', 'manager'] },
      { name: 'Équipes', href: '/teams', icon: UsersRound, roles: ['superadmin', 'manager'] },
    ]
  },
  {
    name: 'Administration',
    icon: Settings,
    items: [
      { name: 'Gestion Utilisateurs', href: '/users', icon: UserPlus, roles: ['superadmin'] },
      { name: 'Paramètres', href: '/admin-settings', icon: Settings, roles: ['superadmin'] },
    ]
  }
];

interface SidebarProps {
  className?: string;
  onShowWhatsNew?: (from: 'sidebar' | 'sidebar-glow') => void;
  /** Make the « Nouveautés » button glow (after the automatic panel closes) until hovered, clicked, or a minute. */
  whatsNewGlow?: boolean;
  onWhatsNewGlowEnd?: () => void;
}

const WHATS_NEW_GLOW_MS = 60_000;

export function Sidebar({ className = '', onShowWhatsNew, whatsNewGlow = false, onWhatsNewGlowEnd }: SidebarProps) {
  const { user, logout } = useAuth();
  const { isLocalPasswordEnabled } = useAuthSettings();
  const { isCollapsed, toggleSidebar } = useSidebar();

  useEffect(() => {
    if (!whatsNewGlow) return;
    const timer = setTimeout(() => onWhatsNewGlowEnd?.(), WHATS_NEW_GLOW_MS);
    return () => clearTimeout(timer);
  }, [whatsNewGlow, onWhatsNewGlowEnd]);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['Staffing'])); // Staffing expanded by default
  
  const [showReportModal, setShowReportModal] = useState(false);

  // Password change modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handleLogout = () => {
    logout();
    setIsMobileOpen(false);
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) {
        newSet.delete(sectionName);
      } else {
        newSet.add(sectionName);
      }
      return newSet;
    });
  };

  const validatePasswordForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = 'Le mot de passe actuel est requis';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'Le nouveau mot de passe doit contenir au moins 6 caractères';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent du mot de passe actuel';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (field: keyof typeof passwordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (passwordSuccess) {
      setPasswordSuccess('');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordSuccess('');
      
      await authApi.changePassword(passwordData.currentPassword, passwordData.newPassword);

      setPasswordSuccess('Mot de passe modifié avec succès !');
      
      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
      
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du changement de mot de passe';
      setPasswordErrors({ 
        general: errorMessage
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Collapsed, each entry is an icon: its name shows beside it on hover and on keyboard focus
  const collapsedTip = (label: string, node: ReactNode, className = 'flex w-full') =>
    isCollapsed ? (
      <Tooltip side="right" content={label} className={className}>
        {node}
      </Tooltip>
    ) : (
      node
    );

  // Filter sections and items based on user role
  const filteredSections = navigationSections.map(section => ({
    ...section,
    items: section.items.filter(item => item.roles.includes(user?.role || ''))
  })).filter(section => section.items.length > 0);

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="p-2 rounded-md bg-white shadow-md border border-gray-200"
        >
          {isMobileOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden bg-black bg-opacity-50"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-40 transform transition-all duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:flex lg:flex-shrink-0
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${className}
      `}>
        <div className="flex flex-col flex-grow bg-white border-r border-neutral-200 pt-6 pb-6 overflow-y-auto scrollbar-hide shadow-soft-lg">
          {/* Header with collapse button */}
          {/* Collapsed, the bar is 4rem wide: the « e » mark above the toggle, not the wordmark beside it */}
          <div className={`flex flex-shrink-0 mb-8 ${isCollapsed ? 'flex-col items-center gap-3' : 'items-center px-5 justify-between'}`}>
            {!isCollapsed ? (
              <div className="flex items-center">
                <Link to="/" className="flex flex-col items-center space-y-2 w-full" title="Accueil">
                  <img 
                    src={logoEquancy} 
                    alt="Equancy" 
                    className="h-8 w-auto"
                  />
                  <p className="text-xs text-neutral-500 text-center">Application staffing</p>
                </Link>
              </div>
            ) : (
              collapsedTip(
                'Accueil',
                <Link to="/" className="flex justify-center w-full">
                  <img src={logoMark} alt="Equancy" className="h-7 w-7" />
                </Link>,
                'flex justify-center'
              )
            )}
            
            {/* Desktop toggle button */}
            {collapsedTip(
              'Étendre le menu',
              <button
                onClick={toggleSidebar}
                className="hidden lg:flex p-2 rounded-xl hover:bg-neutral-100 transition-all duration-200"
                title={isCollapsed ? undefined : 'Réduire le menu'}
                aria-label={isCollapsed ? 'Étendre le menu' : 'Réduire le menu'}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-neutral-600" />
                ) : (
                  <ChevronLeft className="h-4 w-4 text-neutral-600" />
                )}
              </button>,
              'hidden lg:flex'
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            <nav className="flex-1 px-3 space-y-3">
              {filteredSections.map((section, sectionIndex) => {
                const SectionIcon = section.icon;
                const isExpanded = expandedSections.has(section.name);
                
                return (
                  <div key={section.name} className="space-y-1">
                    {/* Section Header. Collapsed, a section cannot fold: a line between groups
                        instead of an icon that looks like a link and does nothing */}
                    {isCollapsed ? (
                      sectionIndex > 0 && (
                        <div role="separator" aria-label={section.name} className="mx-2 border-t border-neutral-200" />
                      )
                    ) : (
                    <button
                      onClick={() => toggleSection(section.name)}
                      className="group flex items-center w-full px-3 py-2.5 text-sm font-semibold text-neutral-700 rounded-xl hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                    >
                      <SectionIcon
                        className="flex-shrink-0 h-5 w-5 mr-3 text-neutral-500 group-hover:text-primary-600 transition-colors duration-200"
                        aria-hidden="true"
                      />
                      <span className="flex-1 text-left">{section.name}</span>
                      <svg
                        className={`ml-2 h-4 w-4 transition-transform duration-200 text-neutral-400 ${
                          isExpanded ? 'transform rotate-90' : ''
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    )}
                    
                    {/* Section Items */}
                    {isExpanded && !isCollapsed && (
                      <div className="ml-8 space-y-1">
                        {section.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <NavLink
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsMobileOpen(false)}
                              className={({ isActive }) =>
                                `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-500 shadow-soft'
                                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 hover:pl-4'
                                }`
                              }
                            >
                              <ItemIcon
                                className={`mr-3 flex-shrink-0 h-4 w-4 transition-colors duration-200`}
                                aria-hidden="true"
                              />
                              {item.name}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Collapsed section items - show as direct nav items */}
                    {isCollapsed && (
                      <div className="space-y-1">
                        {section.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <Tooltip key={item.name} side="right" content={item.name} className="flex w-full">
                              <NavLink
                                to={item.href}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                  `group flex w-full items-center justify-center px-2 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                                    isActive
                                      ? 'bg-primary-100 text-primary-900 shadow-soft'
                                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-600'
                                  }`
                                }
                                aria-label={item.name}
                              >
                                <ItemIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </NavLink>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            
            {/* User info and logout */}
            <div className="px-3 mt-8 pt-6 border-t border-neutral-200">
              {!isCollapsed && (
                <div className="mb-4 px-3 py-3 bg-neutral-50 rounded-xl">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1">
                    Connecté en tant que
                  </p>
                  <p className="text-sm font-semibold text-neutral-900 truncate">
                    {user?.email}
                  </p>
                  <p className="text-xs text-neutral-500 capitalize mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-800">
                    {user?.role}
                  </p>
                  <p className="mt-2 text-[11px] text-neutral-400" title="Version de l'application">
                    Version {__APP_VERSION__}
                  </p>
                </div>
              )}
              {isLocalPasswordEnabled && collapsedTip('Changer mot de passe',
                <button
                  onClick={() => setShowPasswordModal(true)}
                  aria-label={isCollapsed ? 'Changer mot de passe' : undefined}
                  className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium text-neutral-600 rounded-xl hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 mb-2 ${
                    isCollapsed ? 'justify-center' : ''
                  }`}
                >
                  <Key
                    className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${
                      isCollapsed ? '' : 'mr-3'
                    }`}
                    aria-hidden="true"
                  />
                  {!isCollapsed && 'Changer mot de passe'}
                </button>
              )}
              {/* The staffing in Claude reads for managers and superadmins only (MCP `allowed_roles`):
                  the guide is offered to them, in a new tab. */}
              {(user?.role === 'manager' || user?.role === 'superadmin') && (
                // Collapsed, the feature's own tooltip names the entry, beside it: one bubble, not two
                <NewFeature
                  featureKey="claude-guide"
                  badge="dot"
                  className="w-full"
                  side={isCollapsed ? 'right' : undefined}
                  tooltip={isCollapsed ? 'Utiliser dans Claude' : undefined}
                >
                  <a
                    href={CLAUDE_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('claude-guide', 'open', 'sidebar')}
                    className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium text-neutral-600 rounded-xl hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 mb-2 ${
                      isCollapsed ? 'justify-center' : ''
                    }`}
                    title={isCollapsed ? undefined : 'Le staffing dans Claude : la page de prise en main'}
                    aria-label={isCollapsed ? 'Utiliser dans Claude' : undefined}
                  >
                    <Bot
                      className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${isCollapsed ? '' : 'mr-3'}`}
                      aria-hidden="true"
                    />
                    {!isCollapsed && (
                      <>
                        Utiliser dans Claude
                        <ExternalLink className="ml-auto h-3.5 w-3.5 text-neutral-400" aria-hidden="true" />
                      </>
                    )}
                  </a>
                </NewFeature>
              )}
              {onShowWhatsNew && collapsedTip('Nouveautés',
                <button
                  onClick={() => onShowWhatsNew(whatsNewGlow ? 'sidebar-glow' : 'sidebar')}
                  aria-label={isCollapsed ? 'Nouveautés' : undefined}
                  onMouseEnter={() => whatsNewGlow && onWhatsNewGlowEnd?.()}
                  className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 mb-2 ${
                    isCollapsed ? 'justify-center' : ''
                  } ${whatsNewGlow ? 'animate-glow bg-primary-50 text-primary-700 ring-2 ring-primary-300' : 'text-neutral-600'}`}
                >
                  <Sparkles
                    className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${isCollapsed ? '' : 'mr-3'}`}
                    aria-hidden="true"
                  />
                  {!isCollapsed && 'Nouveautés'}
                </button>
              )}
              <NewFeature
                featureKey="report-problem"
                badge="dot"
                className="w-full"
                side={isCollapsed ? 'right' : undefined}
                tooltip={isCollapsed ? 'Signaler un souci' : undefined}
              >
                <button
                  onClick={() => {
                    track('report-problem', 'open', 'sidebar');
                    setShowReportModal(true);
                  }}
                  aria-label={isCollapsed ? 'Signaler un souci' : undefined}
                  className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium text-neutral-600 rounded-xl hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 mb-2 ${
                    isCollapsed ? 'justify-center' : ''
                  }`}
                >
                  <LifeBuoy
                    className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${isCollapsed ? '' : 'mr-3'}`}
                    aria-hidden="true"
                  />
                  {!isCollapsed && 'Signaler un souci'}
                </button>
              </NewFeature>
              {collapsedTip('Se déconnecter',
              <button
                onClick={handleLogout}
                aria-label={isCollapsed ? 'Se déconnecter' : undefined}
                className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium text-neutral-600 rounded-xl hover:bg-error-50 hover:text-error-700 transition-all duration-200 ${
                  isCollapsed ? 'justify-center' : ''
                }`}
              >
                <LogOut
                  className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${
                    isCollapsed ? '' : 'mr-3'
                  }`}
                  aria-hidden="true"
                />
                {!isCollapsed && 'Se déconnecter'}
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <ReportProblemModal isOpen={showReportModal} onClose={() => setShowReportModal(false)} role={user?.role} />

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordErrors({});
          setPasswordSuccess('');
        }}
        title="Changer le mot de passe"
        size="md"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {passwordErrors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{passwordErrors.general}</p>
            </div>
          )}
          
          {passwordSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-sm text-green-600">{passwordSuccess}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe actuel *
            </label>
            <div className="relative">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Entrez votre mot de passe actuel"
                className={passwordErrors.currentPassword ? 'border-red-500' : ''}
                disabled={passwordLoading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                {showPasswords.current ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe *
            </label>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Minimum 6 caractères"
                className={passwordErrors.newPassword ? 'border-red-500' : ''}
                disabled={passwordLoading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                {showPasswords.new ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le nouveau mot de passe *
            </label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Répéter le nouveau mot de passe"
                className={passwordErrors.confirmPassword ? 'border-red-500' : ''}
                disabled={passwordLoading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setPasswordErrors({});
                setPasswordSuccess('');
              }}
              disabled={passwordLoading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              loading={passwordLoading}
              className="min-w-32 bg-blue-600 hover:bg-blue-700"
            >
              Changer le mot de passe
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}